import { useCallback, useRef } from 'react';
import { Popper } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { PersonaButton } from 'src/components/PersonaButton';
import { PersonaDetailsPanel } from 'src/components/PersonaDetailsPanel';
import { PersonaMoreButton } from 'src/components/PersonaMoreButton';
import { EMPTY_PERSONA } from 'src/data/persona';

import { usePopoverLogic } from './usePopoverLogic';
import { useStyles } from './useStyles';

export const PersonaSelector = ({
  className,
  selectedPersonaId,
  onSelect,
  onEdit,
  onDelete,
  onSave,
  onShowMore,
  disabled,
  isLoading,
  onSeen,
  hasHiddenSuggestedPersonas,
  visiblePersonas,
  reducedHeight,
  personaCount,
  showMoreButton,
  brandVoices,
  hasNonDisplayedPersonas,
  isSaving,
}) => {
  const styles = useStyles({ personaCount });
  const rootRef = useRef();

  const { detailsVisibleForPersona, buttonPersonaProps, popperProps } = usePopoverLogic();
  const popperVisible = Boolean(detailsVisibleForPersona);

  const handlePersonaClick = useCallback(
    (event, data) => {
      onSelect(data);
    },
    [onSelect]
  );

  return (
    <div
      className={cx(styles.root, className)}
      ref={rootRef}
      data-testid="PersonaSelector"
    >
      <PersonaButton
        className={cx(styles.buttonPersona, reducedHeight && styles.buttonPersonaReducedHeight)}
        data={EMPTY_PERSONA}
        onClick={handlePersonaClick}
        selected={!selectedPersonaId}
        disabled={disabled}
      />

      {visiblePersonas?.map((persona, i) => (
        <PersonaButton
          className={cx(styles.buttonPersona, reducedHeight && styles.buttonPersonaReducedHeight)}
          key={`persona-${persona?.id}-${i}`}
          data={persona}
          onClick={handlePersonaClick}
          selected={persona?.id === selectedPersonaId}
          disabled={disabled}
          onSeen={onSeen}
          isLoading={isLoading}
          {...buttonPersonaProps}
        />
      ))}

      {showMoreButton && (
        <PersonaMoreButton
          className={cx(styles.buttonMore, reducedHeight && styles.buttonPersonaReducedHeight)}
          onClick={onShowMore}
          disabled={disabled}
          badge={!isLoading && hasHiddenSuggestedPersonas}
          hasNonDisplayedPersonas={hasNonDisplayedPersonas}
        />
      )}

      {detailsVisibleForPersona && (
        <Popper
          className={styles.popper}
          anchorEl={rootRef.current}
          open={popperVisible}
          placement="top"
          {...popperProps}
        >
          {({ placement }) => (
            <div
              className={cx(styles.popperContentWrapper, {
                [styles.bottom]: placement === 'bottom',
              })}
            >
              <PersonaDetailsPanel
                persona={detailsVisibleForPersona}
                onEdit={onEdit}
                onDelete={onDelete}
                onSave={onSave}
                displayActions
                brandVoices={brandVoices}
                isSaving={isSaving}
              />
            </div>
          )}
        </Popper>
      )}
    </div>
  );
};

PersonaSelector.propTypes = {
  className: PropTypes.string,
  selectedPersonaId: PropTypes.string,
  onSelect: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  onShowMore: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSeen: PropTypes.func,
  hasHiddenSuggestedPersonas: PropTypes.bool,
  reducedHeight: PropTypes.bool,
  showMoreButton: PropTypes.bool,
  visiblePersonas: PropTypes.arrayOf(PropTypes.shape({})),
  personaCount: PropTypes.number,
  brandVoices: PropTypes.arrayOf(PropTypes.shape({})),
  hasNonDisplayedPersonas: PropTypes.bool,
  isSaving: PropTypes.bool,
};

PersonaSelector.defaultProps = {
  className: '',
  selectedPersonaId: null,
  onSelect: null,
  onEdit: null,
  onDelete: null,
  onSave: null,
  onShowMore: null,
  disabled: false,
  isLoading: false,
  onSeen: null,
  hasHiddenSuggestedPersonas: false,
  reducedHeight: false,
  showMoreButton: true,
  visiblePersonas: null,
  personaCount: 0,
  brandVoices: [],
  hasNonDisplayedPersonas: false,
  isSaving: false,
};
